import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ICalendarEvent } from 'app/interfaces/event.interface';
import { DateTimeService } from 'app/services/datetime.service';
import { DateTime } from 'luxon';
import { IJourneySessionProgress } from 'modules/journeys/interfaces/journey.interface';
import { JourneyService } from 'modules/journeys/services/journey.service';

@Component({
	selector: 'appointment-details-session-data',
	templateUrl: './appointment-details-session-data.component.html',
	styleUrls: ['./appointment-details-session-data.component.scss'],
})
export class AppointmentDetailsSessionDataComponent implements OnInit {
	public journeySessionProgress: IJourneySessionProgress;

	sessionModelLabels: { [key: string]: string } = {
		reception_session:
			'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.SESSION_MODEL.RECEPTION',
		coordinated_care:
			'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.SESSION_MODEL.COORDINATED_CARE',
		screening_coordinated_care:
			'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.SESSION_MODEL.SCREENING',
		'Sessão SOS RS':
			'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.SESSION_MODEL.SOS_RS',
		volks_leadership_program:
			'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.SESSION_MODEL.VOLKS_LEADERSHIP_PROGRAM',
	};

	validSessionModels: string[] = Object.keys(this.sessionModelLabels);

	@Input()
	public event: ICalendarEvent;

	constructor(
		public translateService: TranslateService,
		public dateTimeService: DateTimeService,
		public journeyService: JourneyService
	) {}

	public ngOnInit(): void {
		if (this.isCoordinatedCareSession()) {
			this.getJourneySessionProgress();
		}
	}

	public isCoordinatedCareSession(): boolean {
		return this.event.subscription?.coordinatedCareType === 'therapy_sessions';
	}

	public getJourneySessionProgress(): void {
		this.journeyService
			.getJourneySessionProgress(this.event.profileId, this.event.id)
			.subscribe((journeySessionProgress: IJourneySessionProgress) => {
				this.journeySessionProgress = journeySessionProgress;
			});
	}

	public getSessionTypeLabel(): string {
		if (this.event.professionalAddress) {
			return this.translateService.instant(
				'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.LOCATION.ON_SITE'
			);
		}

		return this.translateService.instant(
			'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.LOCATION.ONLINE'
		);
	}

	public getSessionStatusLabel(): string {
		if (this.event.status === 'invite') {
			return this.translateService.instant(
				'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.STATUS.INVITE'
			);
		}

		if (this.event.status === 'reschedule') {
			return this.translateService.instant(
				'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.STATUS.RESCHEDULED'
			);
		}

		if (this.event.status === 'reschedule_request') {
			return this.translateService.instant(
				'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.STATUS.RESCHEDULE_REQUEST'
			);
		}

		return this.translateService.instant(
			'APPOINTMENTS.DETAILS.BODY.SESSION_DATA.STATUS.CONFIRMED'
		);
	}

	public getSessionStatusIcon(): string {
		if (
			this.event.status === 'invite' ||
			this.event.status === 'reschedule_request'
		) {
			return 'send';
		}

		if (this.event.status === 'reschedule') {
			return 'calendar-time';
		}

		return 'check';
	}

	public shouldShowInviteExpirationDate(): boolean {
		return this.event.status === 'invite' && !!this.event.inviteExpirationDate;
	}

	public shouldShowCustomTag(): boolean {
		return (
			this.dateTimeService.dateTimeIsAfter(this.event.dayEnd) ||
			['reschedule_request', 'reschedule'].includes(this.event.status)
		);
	}

	public formatDateTime(date: string): string {
		return DateTime.fromISO(date)
			.setLocale('pt-BR')
			.toFormat("dd 'de' MMMM 'às' HH:mm");
	}

	public formatEventDate(date: Date): string {
		return this.dateTimeService.getInFullDate(date);
	}

	public shouldShowSessionModel(): boolean {
		return this.validSessionModels.includes(
			this.event.subscription?.sessionModel
		);
	}

	public getSessionModelLabel(): string {
		const translatedLabel =
			this.sessionModelLabels[this.event.subscription.sessionModel];

		return translatedLabel
			? this.translateService.instant(translatedLabel)
			: '';
	}
}
